import { useState } from 'react';
import { Search } from 'lucide-react';
import { useCompanies } from '@/hooks/useCompany';
import { Spinner, EmptyState, Pagination } from '@/components/common';
import { cn } from '@/lib/utils';
import type { CompanyFilter } from '@/types';
import CompanyCard from '@/components/card/CompanyCard';

const LIMIT = 12;

export default function CompanyListPage() {
  const [filters, setFilters] = useState<CompanyFilter>({
    page: 1,
    limit: LIMIT,
  });
  const [search, setSearch] = useState('');

  const { data, isLoading, isFetching } = useCompanies(filters);

  const handleSearch = () => {
    setFilters((f) => ({ ...f, search: search.trim() || undefined, page: 1 }));
  };

  const resetAll = () => {
    setSearch('');
    setFilters({
      page: 1,
      limit: LIMIT,
    });
  };

  const items = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="section-label">Việc làm</div>
        <div className="flex items-end justify-between">
          <h1 className="section-title mb-0">Danh sách công ty</h1>
          {total > 0 && (
            <span className="text-sm" style={{ color: 'var(--text-3)' }}>
              {total} doanh nghiệp
            </span>
          )}
        </div>
      </div>

      {/* ── Search panel ── */}
      <div
        className="rounded-2xl border mb-6 overflow-hidden"
        style={{
          background: 'var(--surface-0)',
          borderColor: 'var(--border-c)',
        }}
      >
        <div className="p-4 flex gap-3 items-center">
          <div className="fnb-search flex-1 h-10">
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Tìm tên công ty, ngành nghề..."
              className="flex-1 px-4 text-sm bg-transparent outline-none"
              style={{ color: 'var(--text-1)' }}
            />
            <button onClick={handleSearch} className="w-10 h-full flex items-center justify-center shrink-0" style={{ background: 'var(--brand)', color: '#fff' }}>
              <Search size={15} />
            </button>
          </div>

          <button
            onClick={resetAll}
            className="px-4 h-10 text-sm rounded-lg border hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors shrink-0"
            style={{ borderColor: 'var(--border-c)', color: 'var(--text-3)' }}
          >
            Xóa lọc
          </button>
        </div>
      </div>

      {/* ── Results ── */}
      {isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <EmptyState title="Không tìm thấy công ty" description="Thử thay đổi từ khóa tìm kiếm khác" icon="🏢" />
      ) : (
        <>
          <div className={cn('grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-4 transition-opacity', isFetching && 'opacity-60')}>
            {items.map((co) => (
              <CompanyCard key={co.id} company={co} />
            ))}
          </div>

          {/* Pagination */}
          <Pagination page={filters.page ?? 1} totalPages={totalPages} onChange={(p) => setFilters((f) => ({ ...f, page: p }))} />
        </>
      )}
    </div>
  );
}
